import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getVendors,
  deleteVendor,
  toggleVendorStatus,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaPrint,
  FaFileExcel,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import {
  chunkArray,
  calculatePdfPagination,
} from "../../../../shared/utils.js/utils";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import {
  saveVendorsCache,
  getVendorsCache,
  generateVendorsCacheKey,
  getAllVendorsCacheForQuery,
  clearOldVendorsCache,
  isOnline,
  removeVendorFromCache,
} from "../../../../shared/utils.js/offlineQueue";
import { makeOfflineRequest } from "../../../../shared/utils.js/offlineRequestManager";

const Vendors = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const {
    vendors: reduxVendors,
    vendorsPagination: reduxVendorsPagination,
    isLoading,
    error,
    success,
  } = useSelector(state => state.auth);
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const [vendors, setVendors] = useState([]);
  const [vendorsPagination, setVendorsPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [perPage, setPerPage] = useState(10); // القيمة المطبقة فعلياً
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const [pdfItemsToMeasure, setPdfItemsToMeasure] = useState([]);
  const [pendingPdfAction, setPendingPdfAction] = useState(null); // 'preview' | 'print'

  // ─── PDF Pagination Engine (shared calculatePdfPagination) ──────────────────
  useEffect(() => {
    if (pdfItemsToMeasure.length > 0) {
      const timer = setTimeout(async () => {
        const elements = {
          topHeader: document.getElementById("vendors-pdf-measure-top-header"),
          thead: document.getElementById("vendors-pdf-measure-thead"),
          footer: document.getElementById("vendors-pdf-measure-footer"),
          tbody: document.getElementById("vendors-pdf-measure-tbody"),
        };

        const estimatedRowHeight = 28;
        const estimatedAvailable = 1033 - 18 - 10 - 180 - 32 - 120 - 15;
        const dynamicFallbackSize = Math.max(
          5,
          Math.floor(estimatedAvailable / estimatedRowHeight),
        );

        const chunks = calculatePdfPagination(pdfItemsToMeasure, elements, {
          pageHeight: 1033,
          containerTopPadding: 18,
          containerBottomPadding: 10,
          safeMargin: 15,
          fallbackSize: dynamicFallbackSize,
          defaultHeaderHeight: 180,
          defaultTableHeaderHeight: 32,
          defaultFooterHeight: 120,
          defaultRowHeight: 28,
        });

        setPdfChunks(chunks);

        if (pendingPdfAction === "preview") {
          setIsPreviewOpen(true);
        } else if (pendingPdfAction === "print") {
          setIsGeneratingPdf(true);
          // Small wait to allow pdfChunks to render in the DOM (vendors-pdf-all-${i})
          await new Promise(resolve => setTimeout(resolve, 800));

          if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
          }

          const pdf = new jsPDF("p", "mm", "a4");
          const pdfWidth = pdf.internal.pageSize.getWidth();

          let printedPagesCount = 0;
          for (let i = 0; i < chunks.length; i++) {
            const element = document.getElementById(`vendors-pdf-all-${i}`);
            if (!element) continue;

            const canvas = await html2canvas(element, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;

            if (printedPagesCount > 0) pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
            printedPagesCount++;
          }

          if (printedPagesCount > 0) {
            const fileName = t("vendors.export.vendorsFileName", {
              defaultValue: isRTL ? "الموردين" : "Vendors",
            });
            const dateStr = new Date().toISOString().split("T")[0];
            const filename =
              pdfType === "single" && chunks[0]?.items[0]
                ? `ACE_Vendor_${chunks[0].items[0].code || chunks[0].items[0].id}_${dateStr}.pdf`
                : `ACE_${fileName}_${dateStr}.pdf`;
            pdf.save(filename);
            toast.success(
              t("vendors.print.success", {
                defaultValue: "تم طباعة الموردين بنجاح",
              }),
              { rtl: isRTL },
            );
          } else {
            toast.error(
              t("vendors.print.error", {
                defaultValue: "حدث خطأ أثناء إنشاء ملف الموردين",
              }),
              { rtl: isRTL },
            );
          }
          setIsGeneratingPdf(false);
        }

        // Reset measurement items and action states
        setPdfItemsToMeasure([]);
        setPendingPdfAction(null);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [pdfItemsToMeasure, pendingPdfAction, pdfType, isRTL, t]);

  const activeFiltersForPdf = useMemo(() => {
    const parts = [];
    if (searchTerm) {
      parts.push(
        `${t("vendors.search", { defaultValue: "Search" })}: ${searchTerm}`,
      );
    }
    if (isActiveFilter === "1") {
      parts.push(
        `${t("vendors.tableHeaders.status", { defaultValue: "Status" })}: ${t("vendors.statuses.active", { defaultValue: "Active" })}`,
      );
    } else if (isActiveFilter === "0") {
      parts.push(
        `${t("vendors.tableHeaders.status", { defaultValue: "Status" })}: ${t("vendors.statuses.inactive", { defaultValue: "Inactive" })}`,
      );
    }
    return parts.join(" | ");
  }, [searchTerm, isActiveFilter, t]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const shouldNavigateToLastPageRef = useRef(false);
  const offlineToastShownRef = useRef(false);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle("sidenav.vendors");
  }, [setTitle]);

  // Sync Redux vendors into local state (used for table + offline handling)
  useEffect(() => {
    if (Array.isArray(reduxVendors)) {
      setVendors(reduxVendors);
      setVendorsPagination(reduxVendorsPagination || null);
    }
  }, [reduxVendors, reduxVendorsPagination]);

  const handleApplyPerPage = () => {
    // عند الضغط على تطبيق: نطبّق القيمة الجديدة ونرجع لأول صفحة
    const total = vendorsPagination?.total || 0;
    const maxValue = total > 0 ? total : 1000;
    const safeValue = Math.max(1, Math.min(maxValue, perPageInput));
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (success) {
      const isAddSuccess =
        success.includes("إضافة") ||
        success.includes("تم إضافة") ||
        success.toLowerCase().includes("add") ||
        success.toLowerCase().includes("added");

      const now = Date.now();
      const last = lastSuccessRef.current;
      // في حالة الإضافة: نفترض إن صفحة الإضافة نفسها عملت toast، فمش هنكرر هنا
      if (!isAddSuccess) {
        if (
          !last.message ||
          last.message !== success ||
          now - last.time > 2000
        ) {
          toast.success(success, { rtl: isRTL });
          lastSuccessRef.current = { message: success, time: now };
        }
      }

      if (isAddSuccess) {
        // بعد إضافة مورد جديد: نعلِّم إننا محتاجين نروح لآخر صفحة
        shouldNavigateToLastPageRef.current = true;
      }
      // Don't refresh on delete or toggle status - they update optimistically
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (
      vendorsPagination &&
      vendorsPagination.last_page > 0 &&
      shouldNavigateToLastPageRef.current
    ) {
      const lastPage = vendorsPagination.last_page;
      if (currentPage !== lastPage) {
        setCurrentPage(lastPage);
      }
      shouldNavigateToLastPageRef.current = false; // إعادة تعيين الـ flag
    }
  }, [vendorsPagination, currentPage]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch]);

  const handleDelete = vendor => {
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!vendorToDelete) return;
    const idToDelete = vendorToDelete.id;
    const vendorToDeleteBackup = vendorToDelete;
    setShowDeleteModal(false);
    setVendorToDelete(null);

    // Optimistically remove from local state first
    const updatedVendors = vendors.filter(vendor => vendor.id !== idToDelete);
    setVendors(updatedVendors);

    try {
      // Prepare query params for cache update
      const queryParams = {
        page: currentPage,
        per_page: perPage,
      };
      const cacheKey = generateVendorsCacheKey(queryParams);

      try {
        // Try to use offline request manager first
        const result = await makeOfflineRequest(
          `/vendors/${idToDelete}`,
          "DELETE",
          null,
        );
        toast.success(
          t("vendors.messages.deleteSuccess", {
            defaultValue: "تم حذف المورد بنجاح",
          }),
          { rtl: isRTL },
        );

        // Update pagination after successful deletion
        if (vendorsPagination && vendorsPagination.total > 0) {
          setVendorsPagination({
            ...vendorsPagination,
            total: Math.max(0, vendorsPagination.total - 1),
          });
        }

        // Update IndexedDB cache after successful deletion
        await saveVendorsCache(
          cacheKey,
          updatedVendors,
          vendorsPagination
            ? {
                ...vendorsPagination,
                total: Math.max(0, vendorsPagination.total - 1),
              }
            : null,
        );

        // Don't refresh - data is already updated optimistically
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === "OFFLINE_REQUEST_SAVED") {
          // Remove vendor from all cache entries optimistically
          try {
            await removeVendorFromCache(idToDelete);
          } catch (cacheError) {
            console.error("Error removing vendor from cache:", cacheError);
          }

          // Update pagination after removal
          if (vendorsPagination && vendorsPagination.total > 0) {
            setVendorsPagination({
              ...vendorsPagination,
              total: Math.max(0, vendorsPagination.total - 1),
            });
          }

          toast.info(
            isRTL
              ? "سيتم حذف المورد عند عودة الاتصال بالإنترنت"
              : "Vendor will be deleted when connection is restored",
            { rtl: isRTL, autoClose: 5000 },
          );

          // Don't refresh - data is already updated optimistically
          return;
        }

        // If it's a client error (4xx), don't retry with Redux - just throw the error
        // This prevents duplicate requests and duplicate toast messages
        if (offlineError.status >= 400 && offlineError.status < 500) {
          throw offlineError; // Re-throw to be caught by outer catch block
        }

        // Only use Redux dispatch as fallback for network errors or server errors (5xx)
        // This should rarely happen since makeOfflineRequest handles most cases
        try {
          await dispatch(deleteVendor(idToDelete)).unwrap();
          toast.success(
            t("vendors.messages.deleteSuccess", {
              defaultValue: "تم حذف المورد بنجاح",
            }),
            { rtl: isRTL },
          );

          // Update pagination after successful deletion
          if (vendorsPagination && vendorsPagination.total > 0) {
            setVendorsPagination({
              ...vendorsPagination,
              total: Math.max(0, vendorsPagination.total - 1),
            });
          }

          // Update IndexedDB cache after successful deletion
          await saveVendorsCache(
            cacheKey,
            updatedVendors,
            vendorsPagination
              ? {
                  ...vendorsPagination,
                  total: Math.max(0, vendorsPagination.total - 1),
                }
              : null,
          );

          // Don't refresh - data is already updated optimistically
        } catch (reduxError) {
          // If Redux also fails, throw to outer catch
          throw reduxError;
        }
      }
    } catch (error) {
      // Revert optimistic update on error
      setVendors(prev => {
        const exists = prev.find(vendor => vendor.id === idToDelete);
        if (!exists) {
          return [...prev, vendorToDeleteBackup].sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            return 0;
          });
        }
        return prev;
      });

      // Only show error if it's not already handled by Redux and not OFFLINE_REQUEST_SAVED
      // Redux will set error state and useEffect will show toast for Redux errors
      // For client errors (4xx) from makeOfflineRequest, show error here
      if (error?.message !== "OFFLINE_REQUEST_SAVED") {
        // Check if error is from Redux (has response property from axios)
        const isReduxError =
          error?.response?.data !== undefined && error?.config !== undefined;

        if (!isReduxError) {
          // Error from makeOfflineRequest - show message from response
          // The error message already contains the API response message
          const errorMessage =
            error?.message ||
            t("vendors.messages.deleteError", {
              defaultValue: "فشل حذف المورد",
            });
          toast.error(errorMessage, { rtl: isRTL });
        }
        // If it's a Redux error, useEffect will handle showing the toast
      }
    }
  };

  const handleToggleStatus = async id => {
    try {
      // Find the vendor to get current status
      const vendor = vendors.find(v => v.id === id);
      if (!vendor) return;

      const newIsActive = !vendor.is_active;
      const originalIsActive = vendor.is_active;

      // Optimistically update local state
      const updatedVendors = vendors.map(v =>
        v.id === id ? { ...v, is_active: newIsActive } : v,
      );
      setVendors(updatedVendors);

      // Prepare query params for cache update
      const queryParams = {
        page: currentPage,
        per_page: perPage,
      };
      const cacheKey = generateVendorsCacheKey(queryParams);

      // Helper function to update all cache entries containing this vendor
      const updateAllVendorCaches = async (vendorId, updatedStatus) => {
        try {
          // Get all cached vendors
          const allCachedVendors = await getAllVendorsCacheForQuery({
            per_page: 1000,
          });
          const updatedAllVendors = allCachedVendors.map(v =>
            v.id === vendorId ? { ...v, is_active: updatedStatus } : v,
          );

          // Update current page cache
          await saveVendorsCache(cacheKey, updatedVendors, vendorsPagination);

          // Also update general cache if we have it
          if (updatedAllVendors.length > 0) {
            const total = updatedAllVendors.length;
            const lastPage = Math.max(1, Math.ceil(total / perPage));
            // Update first page cache as general cache
            const generalCacheKey = generateVendorsCacheKey({
              page: 1,
              per_page: perPage,
            });
            const firstPageVendors = updatedAllVendors.slice(0, perPage);
            await saveVendorsCache(generalCacheKey, firstPageVendors, {
              current_page: 1,
              last_page: lastPage,
              per_page: perPage,
              total,
            });
          }
        } catch (cacheError) {
          console.error("Error updating all vendor caches:", cacheError);
        }
      };

      try {
        // Try to use offline request manager first
        // Note: toggle-status endpoint uses PATCH with empty body
        const result = await makeOfflineRequest(
          `/vendors/${id}/toggle-status`,
          "PATCH",
          null,
        );
        toast.success(
          t("vendors.messages.statusUpdated", {
            defaultValue: "تم تحديث الحالة بنجاح",
          }),
          { rtl: isRTL },
        );

        // Update all IndexedDB cache entries
        await updateAllVendorCaches(id, newIsActive);
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === "OFFLINE_REQUEST_SAVED") {
          toast.info(
            isRTL
              ? "سيتم تحديث الحالة عند عودة الاتصال بالإنترنت"
              : "Status will be updated when connection is restored",
            { rtl: isRTL, autoClose: 5000 },
          );
          // Update IndexedDB cache optimistically
          await updateAllVendorCaches(id, newIsActive);
          return;
        }

        // If it's a different error, try the original Redux dispatch as fallback
        await dispatch(
          toggleVendorStatus({ id, newIsActive, originalIsActive }),
        ).unwrap();
        toast.success(
          t("vendors.messages.statusUpdated", {
            defaultValue: "تم تحديث الحالة بنجاح",
          }),
          { rtl: isRTL },
        );

        // Update all IndexedDB cache entries
        await updateAllVendorCaches(id, newIsActive);
      }
    } catch (error) {
      // Revert optimistic update on error
      const vendor = vendors.find(v => v.id === id);
      if (vendor) {
        setVendors(prev =>
          prev.map(v =>
            v.id === id ? { ...v, is_active: !vendor.is_active } : v,
          ),
        );
      }
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message ||
            error?.response?.data?.message ||
            t("vendors.messages.statusUpdateError", {
              defaultValue: "فشل تحديث الحالة",
            });
      toast.error(errorMessage, { rtl: isRTL });
    }
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = value => {
    setIsActiveFilter(value);
    setCurrentPage(1);
  };

  const handleSortChange = field => {
    if (sortBy === field) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setIsActiveFilter("");
    setSortBy("");
    setSortDirection("asc");
    setCurrentPage(1);
  };

  // Fetch vendors list with offline cache support
  const fetchVendors = useCallback(
    async (page = currentPage, perPageValue = perPage) => {
      try {
        const queryParams = {
          page,
          per_page: perPageValue,
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(isActiveFilter !== "" ? { is_active: isActiveFilter } : {}),
          ...(sortBy ? { sort_by: sortBy, sort_direction: sortDirection } : {}),
        };
        const cacheKey = generateVendorsCacheKey(queryParams);

        // Step 1: try to load from IndexedDB first
        let cachedData = null;
        try {
          cachedData = await getVendorsCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.vendors)) {
            setVendors(cachedData.vendors);
            setVendorsPagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error("Vendors: error loading from cache:", cacheError);
        }

        // Step 2: sync with API when online
        if (isOnline()) {
          try {
            const payload = await dispatch(getVendors(queryParams)).unwrap();

            // Normalize response
            let items = [];
            let paginationMeta = null;
            if (Array.isArray(payload?.data)) {
              items = payload.data;
              paginationMeta = payload.meta || null;
            } else if (Array.isArray(payload)) {
              items = payload;
              paginationMeta = null;
            }

            // Update cache
            try {
              await saveVendorsCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error("Vendors: error saving to cache:", saveError);
            }

            // Update local UI
            if (items.length > 0) {
              setVendors(items);
              setVendorsPagination(paginationMeta);
            }
          } catch (apiError) {
            console.error("Vendors: API fetch error:", apiError);
            // Try to fall back to all cached vendors for pagination
            const allCached = await getAllVendorsCacheForQuery({
              per_page: perPageValue,
            });
            if (Array.isArray(allCached) && allCached.length > 0) {
              const total = allCached.length;
              const lastPage = Math.max(1, Math.ceil(total / perPageValue));
              const safePage = Math.min(page, lastPage);
              const startIndex = (safePage - 1) * perPageValue;
              const pageItems = allCached.slice(
                startIndex,
                startIndex + perPageValue,
              );

              setVendors(pageItems);
              setVendorsPagination({
                current_page: safePage,
                last_page: lastPage,
                per_page: perPageValue,
                total,
              });

              if (!offlineToastShownRef.current) {
                offlineToastShownRef.current = true;
                toast.warning(
                  isRTL
                    ? "لا يمكن الاتصال بالخادم. يتم عرض بيانات الموردين المحفوظة محلياً."
                    : "Cannot connect to server. Showing locally saved vendors.",
                  { rtl: isRTL, autoClose: 3000 },
                );
              }
            } else {
              const msg =
                typeof apiError === "string"
                  ? apiError
                  : apiError?.message ||
                    t("vendors.messages.fetchError", {
                      defaultValue: "فشل تحميل الموردين",
                    });
              toast.error(msg, { rtl: isRTL });
              setVendors([]);
              setVendorsPagination(null);
            }
          }
        } else {
          // Offline: rely on cache if available
          const allCached = await getAllVendorsCacheForQuery({
            per_page: perPageValue,
          });
          if (Array.isArray(allCached) && allCached.length > 0) {
            const total = allCached.length;
            const lastPage = Math.max(1, Math.ceil(total / perPageValue));
            const safePage = Math.min(page, lastPage);
            const startIndex = (safePage - 1) * perPageValue;
            const pageItems = allCached.slice(
              startIndex,
              startIndex + perPageValue,
            );

            setVendors(pageItems);
            setVendorsPagination({
              current_page: safePage,
              last_page: lastPage,
              per_page: perPageValue,
              total,
            });

            if (!offlineToastShownRef.current) {
              offlineToastShownRef.current = true;
              toast.info(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت. يتم عرض بيانات الموردين المحفوظة محلياً."
                  : "No internet connection. Showing locally saved vendors.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
          } else {
            if (!offlineToastShownRef.current) {
              offlineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات موردين محفوظة."
                  : "No internet connection and no locally saved vendors.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            setVendors([]);
            setVendorsPagination(null);
          }
        }
      } catch (error) {
        console.error("Vendors: error in fetchVendors:", error);
        const msg =
          typeof error === "string"
            ? error
            : error?.message ||
              t("vendors.messages.fetchError", {
                defaultValue: "فشل تحميل الموردين",
              });
        toast.error(msg, { rtl: isRTL });
        setVendors([]);
        setVendorsPagination(null);
      }
    },
    [
      dispatch,
      t,
      isRTL,
      currentPage,
      perPage,
      searchTerm,
      isActiveFilter,
      sortBy,
      sortDirection,
    ],
  );

  // Clear old cache once and fetch on mount / pagination change
  useEffect(() => {
    clearOldVendorsCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchVendors(currentPage, perPage);
  }, [currentPage, perPage, fetchVendors]);

  // Auto-refresh when coming back online (to sync data after offline requests complete)
  useEffect(() => {
    const handleOnline = () => {
      // Refresh immediately when online (retries happen in background)
      fetchVendors(currentPage, perPage);
    };

    // Listen for custom event when offline requests are retried
    const handleOfflineRequestsRetried = event => {
      // Only refresh if there were actual retries (count > 0)
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results && detail.results.length > 0) {
        // Only refresh if at least one request was successfully retried
        const hasSuccessfulRetries = detail.results.some(
          r => r.success === true,
        );
        if (hasSuccessfulRetries) {
          // Check if any of the successful retries were DELETE requests for vendors
          const hasVendorDelete = detail.results.some(
            r =>
              r.success === true &&
              r.request?.method === "DELETE" &&
              r.request?.url?.includes("/vendors/"),
          );
          if (hasVendorDelete) {
            // Refresh vendors list to reflect deleted items
            fetchVendors(currentPage, perPage);
          }
        }
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener(
      "offlineRequestsRetried",
      handleOfflineRequestsRetried,
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener(
        "offlineRequestsRetried",
        handleOfflineRequestsRetried,
      );
    };
  }, [currentPage, perPage, fetchVendors]);

  // Removed polling mechanism - it was causing infinite loop
  // Data updates are now handled via offlineRequestsRetried event

  const formatDate = dateString => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      const locale = "en-US";
      return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Handle print single vendor - Download PDF
  // Handle print single vendor - Download PDF
  const handlePrintVendor = vendor => {
    if (!vendor) {
      toast.error(
        t("vendors.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
        { rtl: isRTL },
      );
      return;
    }
    setPdfType("single");
    setPendingPdfAction("print");
    setPdfItemsToMeasure([vendor]);
  };

  // Handle preview single vendor
  const handlePreviewVendor = vendor => {
    if (!vendor) {
      toast.error(
        t("vendors.preview.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    setPdfType("single");
    setPendingPdfAction("preview");
    setPdfItemsToMeasure([vendor]);
  };

  // Handle preview all vendors
  const handlePreviewAll = async () => {
    try {
      const queryParams = {
        page: 1,
        per_page: perPage,
      };

      const allCachedVendors = await getAllVendorsCacheForQuery(queryParams);

      let allVendors = [];
      let usingCachedData = false;

      if (isOnline()) {
        try {
          const total =
            vendorsPagination?.total || allCachedVendors.length || 0;
          if (total === 0 && allCachedVendors.length === 0) {
            toast.error(
              t("vendors.print.noData", {
                defaultValue: "لا توجد بيانات للمعاينة",
              }),
              { rtl: isRTL },
            );
            return;
          }

          const params = {
            page: 1,
            per_page: total || allCachedVendors.length || 1000,
            ...(searchTerm ? { search: searchTerm } : {}),
            ...(isActiveFilter !== "" ? { is_active: isActiveFilter } : {}),
            ...(sortBy
              ? { sort_by: sortBy, sort_direction: sortDirection }
              : {}),
          };

          const result = await dispatch(getVendors(params));
          let fetchedVendors = [];
          if (result.payload?.data?.data) {
            fetchedVendors = result.payload.data.data;
          } else if (result.payload?.data) {
            fetchedVendors = Array.isArray(result.payload.data)
              ? result.payload.data
              : [];
          } else if (Array.isArray(result.payload)) {
            fetchedVendors = result.payload;
          }

          if (fetchedVendors && fetchedVendors.length > 0) {
            allVendors = fetchedVendors;
          } else if (allCachedVendors.length > 0) {
            allVendors = allCachedVendors;
            usingCachedData = true;
          } else if (vendors && vendors.length > 0) {
            allVendors = vendors;
          }
        } catch (apiError) {
          if (allCachedVendors.length > 0) {
            allVendors = allCachedVendors;
            usingCachedData = true;
          } else if (vendors && vendors.length > 0) {
            allVendors = vendors;
          }
        }
      } else {
        if (allCachedVendors.length > 0) {
          allVendors = allCachedVendors;
          usingCachedData = true;
        } else if (vendors && vendors.length > 0) {
          allVendors = vendors;
        }
      }

      if (!allVendors || allVendors.length === 0) {
        toast.error(
          t("vendors.print.noData", {
            defaultValue: "لا توجد بيانات للمعاينة",
          }),
          { rtl: isRTL },
        );
        return;
      }

      setPdfType("all");
      setPendingPdfAction("preview");
      setPdfItemsToMeasure(allVendors);
    } catch (err) {
      console.error("Preview error:", err);
      toast.error(
        t("vendors.print.loadError", { defaultValue: "فشل تحميل البيانات" }),
        { rtl: isRTL },
      );
    }
  };

  // Handle print all vendors
  const handlePrintAll = async () => {
    try {
      const queryParams = {
        page: 1,
        per_page: perPage,
      };

      const allCachedVendors = await getAllVendorsCacheForQuery(queryParams);

      let allVendorsList = [];
      let usingCachedData = false;

      if (isOnline()) {
        try {
          const total =
            vendorsPagination?.total || allCachedVendors.length || 0;
          if (total === 0 && allCachedVendors.length === 0) {
            toast.error(
              t("vendors.print.noData", {
                defaultValue: "لا توجد بيانات للطباعة",
              }),
              { rtl: isRTL },
            );
            return;
          }

          const params = {
            page: 1,
            per_page: total || allCachedVendors.length || 1000,
            ...(searchTerm ? { search: searchTerm } : {}),
            ...(isActiveFilter !== "" ? { is_active: isActiveFilter } : {}),
            ...(sortBy
              ? { sort_by: sortBy, sort_direction: sortDirection }
              : {}),
          };

          const result = await dispatch(getVendors(params));
          let fetchedVendors = [];
          if (result.payload?.data?.data) {
            fetchedVendors = result.payload.data.data;
          } else if (result.payload?.data) {
            fetchedVendors = Array.isArray(result.payload.data)
              ? result.payload.data
              : [];
          } else if (Array.isArray(result.payload)) {
            fetchedVendors = result.payload;
          }

          if (fetchedVendors && fetchedVendors.length > 0) {
            allVendorsList = fetchedVendors;
          } else if (allCachedVendors.length > 0) {
            allVendorsList = allCachedVendors;
            usingCachedData = true;
          } else if (vendors && vendors.length > 0) {
            allVendorsList = vendors;
          }
        } catch (apiError) {
          if (allCachedVendors.length > 0) {
            allVendorsList = allCachedVendors;
            usingCachedData = true;
          } else if (vendors && vendors.length > 0) {
            allVendorsList = vendors;
          }
        }
      } else {
        if (allCachedVendors.length > 0) {
          allVendorsList = allCachedVendors;
          usingCachedData = true;
        } else if (vendors && vendors.length > 0) {
          allVendorsList = vendors;
        }
      }

      if (!allVendorsList || allVendorsList.length === 0) {
        toast.error(
          t("vendors.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
          { rtl: isRTL },
        );
        return;
      }

      setPdfType("all");
      setPendingPdfAction("print");
      setPdfItemsToMeasure(allVendorsList);
    } catch (error) {
      console.error("Vendors PDF error:", error);
      toast.error(
        t("vendors.print.error", {
          defaultValue: "حدث خطأ أثناء إنشاء ملف الموردين",
        }),
        { rtl: isRTL },
      );
    }
  };

  // Handle export single vendor to Excel
  const handleExportVendorToExcel = vendor => {
    const data = [
      {
        [t("vendors.tableHeaders.code", { defaultValue: "الكود" })]:
          vendor.code || vendor.id || "—",
        [t("vendors.tableHeaders.nameAr", { defaultValue: "الاسم بالعربية" })]:
          vendor.name_ar || "—",
        [t("vendors.tableHeaders.nameEn", {
          defaultValue: "الاسم بالإنجليزية",
        })]: vendor.name_en || "—",
        [t("vendors.tableHeaders.contactPerson", {
          defaultValue: "الشخص المسؤول",
        })]: vendor.contact_person || "—",
        [t("vendors.tableHeaders.email", {
          defaultValue: "البريد الإلكتروني",
        })]: vendor.email || "—",
        [t("vendors.tableHeaders.phone", { defaultValue: "الهاتف" })]:
          vendor.phone || "—",
        [t("vendors.tableHeaders.status", { defaultValue: "الحالة" })]:
          vendor.is_active
            ? t("vendors.active", { defaultValue: "نشط" })
            : t("vendors.inactive", { defaultValue: "غير نشط" }),
        [t("vendors.tableHeaders.createdAt", {
          defaultValue: "تاريخ الإنشاء",
        })]: formatDate(vendor.created_at),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    const sheetName = t("vendors.export.vendorSheetName", {
      defaultValue: isRTL ? "مورد" : "Vendor",
    });
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const identifier = vendor.code || vendor.id;
    const fileNamePrefix = t("vendors.export.vendorFileName", {
      defaultValue: isRTL ? "مورد" : "Vendor",
    });
    const fileName = `${fileNamePrefix}_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("vendors.export.success", { defaultValue: "تم تصدير البيانات بنجاح" }),
      { rtl: isRTL },
    );
  };

  // Handle export all vendors to Excel
  const handleExportAllToExcel = async () => {
    try {
      // Get total from pagination
      const total = vendorsPagination?.total || vendors?.length || 0;

      if (total === 0) {
        toast.error(
          t("vendors.export.noData", {
            defaultValue: "لا توجد بيانات للتصدير",
          }),
          { rtl: isRTL },
        );
        return;
      }

      // Fetch all vendors
      const params = {
        page: 1,
        per_page: total,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(isActiveFilter !== "" ? { is_active: isActiveFilter } : {}),
        ...(sortBy ? { sort_by: sortBy, sort_direction: sortDirection } : {}),
      };

      const result = await dispatch(getVendors(params)).unwrap();
      const allVendors = result?.data || result || vendors || [];

      if (!allVendors || allVendors.length === 0) {
        toast.error(
          t("vendors.export.noData", {
            defaultValue: "لا توجد بيانات للتصدير",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const data = allVendors.map((vendor, index) => ({
        [t("vendors.serialNumber", { defaultValue: "#" })]: index + 1,
        [t("vendors.tableHeaders.code", { defaultValue: "الكود" })]:
          vendor.code || vendor.id || "—",
        [t("vendors.tableHeaders.nameAr", { defaultValue: "الاسم بالعربية" })]:
          vendor.name_ar || "—",
        [t("vendors.tableHeaders.nameEn", {
          defaultValue: "الاسم بالإنجليزية",
        })]: vendor.name_en || "—",
        [t("vendors.tableHeaders.contactPerson", {
          defaultValue: "الشخص المسؤول",
        })]: vendor.contact_person || "—",
        [t("vendors.tableHeaders.email", {
          defaultValue: "البريد الإلكتروني",
        })]: vendor.email || "—",
        [t("vendors.tableHeaders.phone", { defaultValue: "الهاتف" })]:
          vendor.phone || "—",
        [t("vendors.tableHeaders.status", { defaultValue: "الحالة" })]:
          vendor.is_active
            ? t("vendors.active", { defaultValue: "نشط" })
            : t("vendors.inactive", { defaultValue: "غير نشط" }),
        [t("vendors.tableHeaders.createdAt", {
          defaultValue: "تاريخ الإنشاء",
        })]: formatDate(vendor.created_at),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      const sheetName = t("vendors.export.vendorsSheetName", {
        defaultValue: isRTL ? "الموردين" : "Vendors",
      });
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const fileNamePrefix = t("vendors.export.vendorsFileName", {
        defaultValue: isRTL ? "الموردين" : "Vendors",
      });
      const fileName = `${fileNamePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("vendors.export.success", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        t("vendors.export.error", { defaultValue: "حدث خطأ أثناء التصدير" }),
        { rtl: isRTL },
      );
    }
  };

  const renderPagination = () => {
    if (!vendorsPagination) return null;

    const { current_page, last_page } = vendorsPagination;
    const pages = [];

    for (let i = 1; i <= last_page; i++) {
      if (
        i === 1 ||
        i === last_page ||
        (i >= current_page - 2 && i <= current_page + 2)
      ) {
        pages.push(i);
      } else if (i === current_page - 3 || i === current_page + 3) {
        pages.push("...");
      }
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          marginTop: "20px",
        }}
      >
        <button
          onClick={() => setCurrentPage(current_page - 1)}
          disabled={current_page === 1}
          style={{
            padding: "8px 12px",
            backgroundColor: current_page === 1 ? "#333" : "#AC2000",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: current_page === 1 ? "not-allowed" : "pointer",
          }}
        >
          {t("btns.previous")}
        </button>
        {pages.map((page, idx) =>
          page === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              style={{ padding: "8px", color: "white" }}
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{
                padding: "8px 12px",
                backgroundColor: page === current_page ? "#AC2000" : "#1a1f2e",
                color: "white",
                border: "1px solid #333",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {page}
            </button>
          ),
        )}
        <button
          onClick={() => setCurrentPage(current_page + 1)}
          disabled={current_page === last_page}
          style={{
            padding: "8px 12px",
            backgroundColor: current_page === last_page ? "#333" : "#AC2000",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: current_page === last_page ? "not-allowed" : "pointer",
          }}
        >
          {t("btns.next")}
        </button>
      </div>
    );
  };

  if (isLoading && (!vendors || vendors.length === 0)) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {t("vendors.loading")}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h1
          style={{ fontSize: "24px", fontWeight: "bold", margin: 0, order: 1 }}
        >
          {t("vendors.title")}
        </h1>
        <div
          style={{
            display: "flex",
            gap: "14px",
            flexWrap: "wrap",
            alignItems: "center",
            order: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ color: "white", fontSize: "14px", whiteSpace: "nowrap" }}
            >
              {t("common.perPage", { defaultValue: "Per Page" })}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() => {
                  const newValue = Math.max(1, perPageInput - 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={vendorsPagination?.total || 1000}
                value={perPageInput}
                onChange={e => {
                  const value = parseInt(e.target.value, 10) || 1;
                  const maxValue = vendorsPagination?.total || 1000;
                  setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                }}
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() => {
                  const maxValue = vendorsPagination?.total || 1000;
                  const newValue = Math.min(maxValue, perPageInput + 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.apply", { defaultValue: "تطبيق" })}
            </button>
          </div>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <FaEye />{" "}
            {t("vendors.preview.all", { defaultValue: "عرض PDF للكل" })}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: isGeneratingPdf ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row-reverse" : "row",
              opacity: isGeneratingPdf ? 0.6 : 1,
            }}
          >
            <FaPrint />{" "}
            {isGeneratingPdf
              ? t("vendors.print.generating", {
                  defaultValue: "جاري الطباعة...",
                })
              : t("vendors.print.all", { defaultValue: "طباعة PDF للكل" })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <FaFileExcel />{" "}
            {t("vendors.export.all", { defaultValue: "تصدير Excel للكل" })}
          </button>
          <button
            onClick={() => navigate("/vendors/add")}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <FaPlus /> {t("vendors.addVendor")}
          </button>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div
        style={{
          display: "flex",
          gap: "15px",
          marginBottom: "20px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ position: "relative", flex: "1", minWidth: "300px" }}>
          <FaSearch
            style={{
              position: "absolute",
              [isRTL ? "right" : "left"]: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#666",
              fontSize: "16px",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder={t("vendors.searchPlaceholder", {
              defaultValue: isRTL ? "ابحث في الموردين..." : "Search vendors...",
            })}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: "100%",
              padding: isRTL ? "12px 45px 12px 15px" : "12px 15px 12px 45px",
              backgroundColor: "#1b3350",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
              boxSizing: "border-box",
            }}
          />
        </div>
        <select
          value={isActiveFilter}
          onChange={e => handleFilterChange(e.target.value)}
          style={{
            padding: "12px 15px",
            backgroundColor: "#1b3350",
            border: "1px solid #333",
            borderRadius: "8px",
            color: "white",
            fontSize: "14px",
            minWidth: "150px",
            cursor: "pointer",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <option value="">
            {t("vendors.allStatuses", {
              defaultValue: isRTL ? "جميع الحالات" : "All Statuses",
            })}
          </option>
          <option value="1">
            {t("vendors.statuses.active", {
              defaultValue: isRTL ? "نشط" : "Active",
            })}
          </option>
          <option value="0">
            {t("vendors.statuses.inactive", {
              defaultValue: isRTL ? "غير نشط" : "Inactive",
            })}
          </option>
        </select>
        {(searchTerm || isActiveFilter !== "" || sortBy) && (
          <button
            onClick={handleResetFilters}
            style={{
              padding: "12px 16px",
              backgroundColor: "#555",
              border: "none",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {t("common.reset", {
              defaultValue: isRTL ? "إعادة تعيين" : "Reset",
            })}
          </button>
        )}
      </div>

      {vendors && vendors.length > 0 ? (
        <>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                backgroundColor: "#1a1f2e",
                borderRadius: "8px",
                overflow: "hidden",
                minWidth: "1200px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#AC2000" }}>
                  <SortableHeader
                    field="#"
                    label="#"
                    width="6%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                    sortable={false}
                  />
                  <SortableHeader
                    field="code"
                    label={t("vendors.tableHeaders.code")}
                    width="12%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                  />
                  <SortableHeader
                    field="name"
                    label={t("vendors.tableHeaders.name")}
                    width="15%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                  />
                  <SortableHeader
                    field="contact_person"
                    label={t("vendors.tableHeaders.contactPerson")}
                    width="18%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                  />
                  <SortableHeader
                    field="email"
                    label={t("vendors.tableHeaders.email")}
                    width="15%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                  />
                  <SortableHeader
                    field="phone"
                    label={t("vendors.tableHeaders.phone")}
                    width="12%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                  />
                  <SortableHeader
                    field="is_active"
                    label={t("vendors.tableHeaders.status")}
                    width="8%"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortChange}
                  />
                  <th
                    style={{
                      padding: "18px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      width: "14%",
                    }}
                  >
                    {t("vendors.tableHeaders.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor, index) => (
                  <tr
                    key={vendor.id}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {(vendorsPagination?.current_page - 1) * perPage +
                        index +
                        1}
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {vendor.code}
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {i18n.language === "ar"
                        ? vendor.name_ar || vendor.name_en || vendor.name || "—"
                        : vendor.name_en ||
                          vendor.name_ar ||
                          vendor.name ||
                          "—"}
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {vendor.contact_person || "—"}
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {vendor.email || "—"}
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {vendor.phone || "—"}
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: vendor.is_active
                            ? "#28a745"
                            : "#dc3545",
                          color: "white",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          display: "inline-block",
                        }}
                      >
                        {vendor.is_active
                          ? t("vendors.statuses.active")
                          : t("vendors.statuses.inactive")}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "18px",
                        textAlign: "center",
                        fontSize: "14px",
                        minWidth: "200px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "nowrap",
                          flexDirection: isRTL ? "row-reverse" : "row",
                        }}
                      >
                        <ActionButton
                          title={t("vendors.actions.view")}
                          onClick={() => handlePreviewVendor(vendor)}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t("vendors.print.single", {
                            defaultValue: "طباعة",
                          })}
                          onClick={() => handlePrintVendor(vendor)}
                          disabled={isGeneratingPdf}
                          bg={isGeneratingPdf ? "#666" : "#17a2b8"}
                          style={{
                            opacity: isGeneratingPdf ? 0.6 : 1,
                            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                          }}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t("vendors.export.single", {
                            defaultValue: "تصدير Excel",
                          })}
                          bg="#28a745"
                          onClick={() => handleExportVendorToExcel(vendor)}
                        >
                          <FaFileExcel />
                        </ActionButton>
                        <ActionButton
                          title={t("vendors.actions.edit")}
                          onClick={() => navigate(`/vendors/edit/${vendor.id}`)}
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t("vendors.actions.delete")}
                          bg="#c0392b"
                          onClick={() => handleDelete(vendor)}
                        >
                          <FaTrash />
                        </ActionButton>
                        <ActionButton
                          title={t("vendors.actions.toggleStatus")}
                          bg="#34495e"
                          onClick={() => handleToggleStatus(vendor.id)}
                        >
                          {vendor.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#888",
            backgroundColor: "#1a1f2e",
            borderRadius: "8px",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          {t("vendors.noVendors")}
        </div>
      )}

      {/************Show Delete Modal***************** */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999999,
          }}
        >
          <div
            style={{
              backgroundColor: "#1a1f2e",
              padding: "30px",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "400px",
              border: "1px solid #333",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <h2
              style={{ color: "white", marginBottom: "20px", fontSize: "20px" }}
            >
              {t("vendors.deleteModal.title")}
            </h2>
            <p style={{ color: "#ccc", marginBottom: "30px" }}>
              {t("vendors.deleteModal.message").replace(
                "{{name}}",
                vendorToDelete?.name_ar ||
                  vendorToDelete?.name_en ||
                  vendorToDelete?.name ||
                  "",
              )}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setVendorToDelete(null);
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {t("vendors.deleteModal.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {t("vendors.deleteModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          PDF MEASUREMENT TEMPLATE
          Always rendered off-screen so calculatePdfPagination can measure
          actual DOM heights at any time without waiting for pdfType state.
      ══════════════════════════════════════════════════════════════════ */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "794px",
          backgroundColor: "#fff",
          padding: "24px",
          boxSizing: "border-box",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            padding: "18px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div id="vendors-pdf-measure-top-header" style={{ flexShrink: 0 }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt=""
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
              }}
              crossOrigin="anonymous"
            />
            <div
              style={{
                marginTop: "12px",
                textAlign: "center",
                marginBottom: "12px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#000080",
                  margin: 0,
                }}
              >
                {t("vendors.pdf.reportTitle", {
                  defaultValue: isRTL ? "تقرير الموردين" : "Vendors Report",
                })}
              </h2>
              <p style={{ fontSize: "12px", color: "#333", margin: "4px 0 0" }}>
                {new Date().toLocaleDateString(isRTL ? "ar-EG" : "en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })}
              </p>
              {activeFiltersForPdf && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#444",
                    margin: "4px 0 0",
                    padding: "4px 8px",
                    backgroundColor: "rgba(0,0,128,0.05)",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                >
                  {activeFiltersForPdf}
                </div>
              )}
            </div>
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "11px",
            }}
          >
            <thead>
              <tr id="vendors-pdf-measure-thead">
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: "700",
                    width: "40px",
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: "700",
                    width: "100px",
                  }}
                >
                  {t("vendors.tableHeaders.code")}
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: isRTL ? "right" : "left",
                    fontWeight: "700",
                  }}
                >
                  {t("vendors.tableHeaders.name")}
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: isRTL ? "right" : "left",
                    fontWeight: "700",
                  }}
                >
                  {t("vendors.tableHeaders.contactPerson")}
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: "left",
                    fontWeight: "700",
                  }}
                >
                  {t("vendors.tableHeaders.email")}
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: isRTL ? "right" : "left",
                    fontWeight: "700",
                  }}
                >
                  {t("vendors.tableHeaders.phone")}
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: "700",
                    width: "80px",
                  }}
                >
                  {t("vendors.tableHeaders.status")}
                </th>
              </tr>
            </thead>
            <tbody id="vendors-pdf-measure-tbody">
              {pdfItemsToMeasure.map((vendor, idx) => {
                const globalIndex = idx + 1;
                return (
                  <tr key={vendor.id || idx}>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {globalIndex}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {vendor.code || vendor.id || "—"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                        verticalAlign: "middle",
                        whiteSpace: "normal",
                        maxWidth: "140px",
                        wordBreak: "break-word",
                      }}
                    >
                      {isRTL
                        ? vendor.name_ar || vendor.name_en || vendor.name
                        : vendor.name_en ||
                          vendor.name_ar ||
                          vendor.name ||
                          "—"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                        verticalAlign: "middle",
                      }}
                    >
                      {vendor.contact_person || "—"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        direction: "ltr",
                        textAlign: "left",
                        verticalAlign: "middle",
                        wordBreak: "break-all",
                        maxWidth: "100px",
                        whiteSpace: "normal",
                      }}
                    >
                      {vendor.email || "—"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                        verticalAlign: "middle",
                        wordBreak: "break-all",
                        maxWidth: "100px",
                        whiteSpace: "normal",
                      }}
                    >
                      {vendor.phone || "—"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {vendor.is_active
                        ? t("vendors.statuses.active")
                        : t("vendors.statuses.inactive")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            id="vendors-pdf-measure-footer"
            style={{ height: "120px", flexShrink: 0 }}
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          HIDDEN PDF PRINT TEMPLATES
      ══════════════════════════════════════════════════════════════════ */}
      {pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`vendors-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              padding: "24px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "hidden",
              }}
            >
              <PdfPageInner
                chunk={chunk.items}
                startIdx={chunk.startIndex}
                chunkIndex={chunkIndex}
                totalChunks={pdfChunks.length}
                showDate={true}
                isRTL={isRTL}
                t={t}
                activeFiltersForPdf={activeFiltersForPdf}
                headerLogo={headerLogo}
                publicUrl={publicUrl}
              />
            </div>
          </div>
        ))}

      {/* ══════════════════════════════════════════════════════════════════════
          PDF PREVIEW MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={
          pdfType === "single"
            ? t("vendors.preview.single", { defaultValue: "معاينة المورد" })
            : t("vendors.title", { defaultValue: "Vendors Report" })
        }
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfType(null);
          setPdfChunks([]);
        }}
        onPrint={
          pdfType === "single"
            ? () => handlePrintVendor(pdfChunks[0]?.items[0])
            : handlePrintAll
        }
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              id={`preview-vendors-pdf-all-${chunkIndex}`}
              style={{
                width: "794px",
                height: "1123px",
                backgroundColor: "#ffffff",
                color: "#000000",
                padding: "24px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                transform: "scale(1.0)",
                transformOrigin: "top center",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                position: "relative",
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "1043px",
                  overflow: "hidden",
                }}
              >
                <PdfPageInner
                  chunk={chunk.items}
                  startIdx={chunk.startIndex}
                  chunkIndex={chunkIndex}
                  totalChunks={pdfChunks.length}
                  showDate={true}
                  isRTL={isRTL}
                  t={t}
                  activeFiltersForPdf={activeFiltersForPdf}
                  headerLogo={headerLogo}
                  publicUrl={publicUrl}
                />
              </div>
            </div>
          ))}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

const PdfPageInner = ({
  chunk,
  startIdx,
  chunkIndex,
  totalChunks,
  showDate,
  isRTL,
  t,
  activeFiltersForPdf,
  headerLogo,
  publicUrl,
}) => {
  const dir = isRTL ? "rtl" : "ltr";
  return (
    <div
      style={{
        border: "2px solid #000",
        height: "100%",
        padding: "18px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* Watermark */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${headerLogo})`,
          backgroundSize: "95%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "100%",
          flex: 1,
        }}
      >
        {/* Header image */}
        <img
          src={`${publicUrl}/HeaderAce.png`}
          alt="Header"
          style={{
            width: "100%",
            height: "auto",
            objectFit: "contain",
            marginBottom: "8px",
            borderBottom: "4px double #000",
            paddingBottom: "8px",
            flexShrink: 0,
          }}
          crossOrigin="anonymous"
        />
        {/* Content area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            paddingBottom: "120px",
          }}
        >
          <div
            style={{
              marginTop: "12px",
              textAlign: "center",
              marginBottom: "12px",
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#000080",
                margin: 0,
                direction: dir,
              }}
            >
              {t("vendors.pdf.reportTitle", {
                defaultValue: isRTL ? "تقرير الموردين" : "Vendors Report",
              })}
            </h2>
            <p style={{ fontSize: "11px", color: "#555", margin: "4px 0 0" }}>
              {new Date().toLocaleDateString(isRTL ? "ar-EG" : "en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
              {totalChunks > 1 &&
                ` — ${t("common.page", { defaultValue: "Page" })} ${chunkIndex + 1} / ${totalChunks}`}
            </p>
            {activeFiltersForPdf && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#444",
                  margin: "4px 0 0",
                  padding: "4px 8px",
                  backgroundColor: "rgba(0,0,128,0.05)",
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              >
                {activeFiltersForPdf}
              </div>
            )}
          </div>
          <div
            style={{
              direction: isRTL ? "rtl" : "ltr",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              flex: 1,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "11px",
                border: "1px solid #000",
                backgroundColor: "rgba(255,255,255,0.7)",
                tableLayout: "auto",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: "center",
                      fontWeight: "700",
                      width: "40px",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: "center",
                      fontWeight: "700",
                      width: "100px",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    {t("vendors.tableHeaders.code")}
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: isRTL ? "right" : "left",
                      fontWeight: "700",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    {t("vendors.tableHeaders.name")}
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: isRTL ? "right" : "left",
                      fontWeight: "700",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    {t("vendors.tableHeaders.contactPerson")}
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: "left",
                      fontWeight: "700",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    {t("vendors.tableHeaders.email")}
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: isRTL ? "right" : "left",
                      fontWeight: "700",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    {t("vendors.tableHeaders.phone")}
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px",
                      textAlign: "center",
                      fontWeight: "700",
                      width: "80px",
                      backgroundColor: "rgba(238, 242, 247, 0.7)",
                    }}
                  >
                    {t("vendors.tableHeaders.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {chunk.map((vendor, idx) => {
                  const globalIndex = startIdx + idx + 1;
                  const rowBg =
                    idx % 2 === 0
                      ? "rgba(255,255,255,0.7)"
                      : "rgba(255,255,255,0.5)";
                  return (
                    <tr key={vendor.id} style={{ backgroundColor: rowBg }}>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                        }}
                      >
                        {globalIndex}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                        }}
                      >
                        {vendor.code || vendor.id || "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          direction: isRTL ? "rtl" : "ltr",
                          textAlign: isRTL ? "right" : "left",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                          whiteSpace: "normal",
                          maxWidth: "140px",
                          wordBreak: "break-word",
                        }}
                      >
                        {isRTL
                          ? vendor.name_ar || vendor.name_en || vendor.name
                          : vendor.name_en ||
                            vendor.name_ar ||
                            vendor.name ||
                            "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          direction: isRTL ? "rtl" : "ltr",
                          textAlign: isRTL ? "right" : "left",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                        }}
                      >
                        {vendor.contact_person || "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          direction: "ltr",
                          textAlign: "left",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                          wordBreak: "break-all",
                          maxWidth: "100px",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.email || "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          direction: isRTL ? "rtl" : "ltr",
                          textAlign: isRTL ? "right" : "left",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                          wordBreak: "break-all",
                          maxWidth: "100px",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.phone || "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          backgroundColor: rowBg,
                        }}
                      >
                        {vendor.is_active
                          ? t("vendors.statuses.active")
                          : t("vendors.statuses.inactive")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Footer — absolutely positioned, never overlaps table */}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          left: "18px",
          right: "18px",
          flexShrink: 0,
        }}
      >
        <img
          src={`${publicUrl}/FooterAce.png`}
          alt="Footer"
          style={{
            width: "100%",
            height: "auto",
            objectFit: "contain",
            borderTop: "4px double #000",
            paddingTop: "8px",
          }}
          crossOrigin="anonymous"
        />
      </div>
    </div>
  );
};

const SortableHeader = ({
  field,
  label,
  width,
  sortBy,
  sortDirection,
  onSort,
  sortable = true,
}) => {
  const isActive = sortBy === field;
  return (
    <th
      style={{
        padding: "18px",
        textAlign: "center",
        color: "white",
        fontWeight: "bold",
        fontSize: "14px",
        width,
        cursor: sortable ? "pointer" : "default",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      onClick={() => sortable && onSort(field)}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          justifyContent: "center",
        }}
      >
        {label}
        {sortable &&
          (isActive ? (
            sortDirection === "asc" ? (
              <FaSortUp style={{ fontSize: "12px" }} />
            ) : (
              <FaSortDown style={{ fontSize: "12px" }} />
            )
          ) : (
            <FaSort style={{ fontSize: "12px", opacity: 0.5 }} />
          ))}
      </span>
    </th>
  );
};

const ActionButton = ({
  children,
  onClick,
  title,
  bg = "#2f3644",
  disabled = false,
  style = {},
}) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      backgroundColor: bg,
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "8px 10px",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "38px",
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
  >
    {children}
  </button>
);

export default Vendors;
